import React, { useState, useEffect } from "react";
import { getCollection, sortHoldstartDates, hasFixedDate } from "../lib/cms";
import { Send, CheckCircle2, AlertCircle, Loader2, Calendar, User, Phone, Mail, MessageSquare, Sparkles, MapPin } from "lucide-react";

export default function ContactForm({ selectedHold, onHoldChange }) {
  const [holdstartDates, setHoldstartDates] = useState([]);
  const [loadingDates, setLoadingDates] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error' | null
  const [errorMessage, setErrorMessage] = useState("");

  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    address: "",
    hold_date: "",
    message: "",
    website_url: "", // Honeypot field
  });

  useEffect(() => {
    async function loadDates() {
      try {
        const dates = await getCollection("holdstart_dates");
        setHoldstartDates(sortHoldstartDates(dates || []));
      } catch (err) {
        console.error("Error loading holdstart dates:", err);
      } finally {
        setLoadingDates(false);
      }
    }
    loadDates();
  }, []);

  // Sync external selectedHold with internal state
  useEffect(() => {
    if (selectedHold !== undefined) {
      setFormData((prev) => ({
        ...prev,
        hold_date: selectedHold,
      }));
    }
  }, [selectedHold]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    
    if (name === "hold_date" && onHoldChange) {
      onHoldChange(value);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitStatus(null);
    setErrorMessage("");

    // Basic validation
    if (!formData.name || !formData.phone || !formData.email || !formData.address || !formData.message) {
      setSubmitStatus("error");
      setErrorMessage("Venligst udfyld alle påkrævede felter (Navn, Telefon, Email, Adresse og Beskrivelse).");
      setSubmitting(false);
      return;
    }

    try {
      const response = await fetch("/_api/webform/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: formData.name,
          phone: formData.phone,
          email: formData.email,
          address: formData.address,
          subject: formData.hold_date ? `Tilmelding til hold: ${formData.hold_date}` : "Generel henvendelse / Spørgsmål",
          message: `Adresse: ${formData.address}\n\nBesked / Fødselsdato:\n${formData.message}`,
          website_url: formData.website_url, // Honeypot
        }),
      });

      if (response.ok) {
        setSubmitStatus("success");
        setFormData({
          name: "",
          phone: "",
          email: "",
          address: "",
          hold_date: "",
          message: "",
          website_url: "",
        });
        if (onHoldChange) onHoldChange("");
      } else {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.message || "Der skete en fejl under afsendelsen. Prøv igen.");
      }
    } catch (err) {
      console.error("Form submission error:", err);
      setSubmitStatus("error");
      setErrorMessage(err.message || "Kunne ikke oprette forbindelse til serveren. Prøv igen senere eller ring direkte.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-white rounded-3xl border border-stone-200 shadow-xl p-6 sm:p-8 lg:p-10 relative overflow-hidden">
      {/* Decorative background element */}
      <div className="absolute top-0 right-0 w-32 h-32 bg-brand-primary/5 rounded-full blur-3xl pointer-events-none"></div>
      
      <div className="relative z-10">
        <div className="flex items-center gap-2 mb-2">
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-brand-primary/10 text-brand-primary text-xs font-bold uppercase tracking-wider">
            <Sparkles className="h-3 w-3" /> Hurtig tilmelding
          </span>
        </div>
        <h3 className="text-2xl sm:text-3xl font-black text-brand-dark mb-2">Tilmeld dig her</h3>
        <p className="text-stone-500 text-sm mb-6">
          Udfyld formularen, så reserverer jeg en plads til dig med det samme og sender dig en bekræftelse på SMS.
        </p>

        {submitStatus === "success" && (
          <div className="mb-8 p-5 bg-emerald-50 border border-emerald-200 rounded-2xl flex items-start gap-3.5 text-emerald-800 animate-fade-in">
            <CheckCircle2 className="h-6 w-6 text-emerald-600 shrink-0 mt-0.5" />
            <div>
              <strong className="font-bold block text-base text-emerald-900 mb-1">Tak for din tilmelding! 🎉</strong>
              <p className="text-sm text-emerald-700 leading-relaxed">
                Jeg har modtaget dine oplysninger og sender dig en bekræftelse på SMS inden for kort tid. Glæder mig til at se dig!
              </p>
            </div>
          </div>
        )}

        {submitStatus === "error" && (
          <div className="mb-8 p-5 bg-rose-50 border border-rose-200 rounded-2xl flex items-start gap-3.5 text-rose-800 animate-fade-in">
            <AlertCircle className="h-6 w-6 text-rose-600 shrink-0 mt-0.5" />
            <div>
              <strong className="font-bold block text-base text-rose-900 mb-1">Der opstod en fejl</strong>
              <p className="text-sm text-rose-700 leading-relaxed">{errorMessage}</p>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Honeypot field - absolutely hidden from humans */}
          <div className="sr-only absolute -z-50 pointer-events-none">
            <label htmlFor="website_url">Do not fill this field</label>
            <input
              type="text"
              id="website_url"
              name="website_url"
              value={formData.website_url}
              onChange={handleChange}
              tabIndex="-1"
              autoComplete="off"
            />
          </div>

          {/* Name */}
          <div className="space-y-1.5">
            <label htmlFor="name" className="block text-xs font-bold uppercase tracking-wider text-stone-500 flex items-center gap-1.5">
              <User className="h-3.5 w-3.5 text-stone-400" />
              Dit fulde navn <span className="text-brand-primary">*</span>
            </label>
            <input
              type="text"
              id="name"
              name="name"
              required
              value={formData.name}
              onChange={handleChange}
              placeholder="F.eks. Sofie Hansen"
              className="w-full px-4 py-3.5 rounded-xl border border-stone-200 bg-stone-50 text-stone-800 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary focus:bg-white transition-all duration-200"
            />
          </div>

          {/* Phone & Email Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label htmlFor="phone" className="block text-xs font-bold uppercase tracking-wider text-stone-500 flex items-center gap-1.5">
                <Phone className="h-3.5 w-3.5 text-stone-400" />
                Telefonnummer <span className="text-brand-primary">*</span>
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                required
                value={formData.phone}
                onChange={handleChange}
                placeholder="F.eks. 30 51 72 69"
                className="w-full px-4 py-3.5 rounded-xl border border-stone-200 bg-stone-50 text-stone-800 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary focus:bg-white transition-all duration-200"
              />
            </div>
            <div className="space-y-1.5">
              <label htmlFor="email" className="block text-xs font-bold uppercase tracking-wider text-stone-500 flex items-center gap-1.5">
                <Mail className="h-3.5 w-3.5 text-stone-400" />
                E-mail <span className="text-brand-primary">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                required
                value={formData.email}
                onChange={handleChange}
                placeholder="din@email.dk"
                className="w-full px-4 py-3.5 rounded-xl border border-stone-200 bg-stone-50 text-stone-800 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary focus:bg-white transition-all duration-200"
              />
            </div>
          </div>

          {/* Address */}
          <div className="space-y-1.5">
            <label htmlFor="address" className="block text-xs font-bold uppercase tracking-wider text-stone-500 flex items-center gap-1.5">
              <MapPin className="h-3.5 w-3.5 text-stone-400" />
              Adresse (vej, postnr. og by) <span className="text-brand-primary">*</span>
            </label>
            <input
              type="text"
              id="address"
              name="address"
              required
              value={formData.address}
              onChange={handleChange}
              placeholder="F.eks. Gugvej 12, 9210 Aalborg SØ"
              className="w-full px-4 py-3.5 rounded-xl border border-stone-200 bg-stone-50 text-stone-800 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary focus:bg-white transition-all duration-200"
            />
          </div>

          {/* Holdstart Date Select */}
          <div className="space-y-1.5">
            <label htmlFor="hold_date" className="block text-xs font-bold uppercase tracking-wider text-stone-500 flex items-center gap-1.5">
              <Calendar className="h-3.5 w-3.5 text-stone-400" />
              Vælg Holdstart (Valgfrit)
            </label>
            <select
              id="hold_date"
              name="hold_date"
              value={formData.hold_date}
              onChange={handleChange}
              className="w-full px-4 py-3.5 rounded-xl border border-stone-200 bg-stone-50 text-stone-800 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary focus:bg-white transition-all duration-200"
            >
              <option value="">-- Vælg et hold --</option>
              {loadingDates ? (
                <option disabled>Henter holdstartsdatoer...</option>
              ) : (
                holdstartDates.map((date) => {
                  const isFixed = hasFixedDate(date.title);
                  const hasCustomTime = date.time && date.time !== "Tidspunkter ikke fastsat";

                  let labelTime = "";
                  if (hasCustomTime) {
                    labelTime = `(${date.time})`;
                  } else if (!isFixed) {
                    labelTime = "(Tidspunkt uafklaret - vis interesse)";
                  }

                  const optionValue = `${date.title} — ${date.category}${hasCustomTime ? ` (${date.time})` : ""}`;

                  const isFullyBooked = Boolean(
                    date.is_fully_booked ||
                    (date.status && (
                      date.status.toLowerCase().includes("fuld") ||
                      date.status.toLowerCase().includes("optaget") ||
                      date.status.toLowerCase().includes("udsolgt")
                    ))
                  );

                  return (
                    <option
                      key={date.id || `${date.title}-${date.category}`}
                      value={optionValue}
                      disabled={isFullyBooked}
                    >
                      {date.title} — {date.category} {labelTime.trim() ? `${labelTime}` : ""} {isFullyBooked ? "(FULD BOOKET)" : ""}
                    </option>
                  );
                })
              )}
            </select>
          </div>

          {/* Message / Description */}
          <div className="space-y-1.5">
            <label htmlFor="message" className="block text-xs font-bold uppercase tracking-wider text-stone-500 flex items-center gap-1.5">
              <MessageSquare className="h-3.5 w-3.5 text-stone-400" />
              Skriv din fødselsdato & kommentarer <span className="text-brand-primary">*</span>
            </label>
            <textarea
              id="message"
              name="message"
              required
              rows="4"
              value={formData.message}
              onChange={handleChange}
              placeholder="Skriv meget gerne din fødselsdato her (vigtigt til papirarbejdet). Skriv også hvis du ønsker automatgear, eller hvis du har andre spørgsmål/kommentarer."
              className="w-full px-4 py-3.5 rounded-xl border border-stone-200 bg-stone-50 text-stone-800 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary focus:bg-white transition-all duration-200 resize-none"
            ></textarea>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={submitting}
            className="w-full py-4 px-6 bg-brand-primary text-white text-sm font-bold rounded-xl shadow-lg hover:bg-brand-primary-hover hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-all duration-200 cursor-pointer"
          >
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Sender din tilmelding...
              </>
            ) : (
              <>
                <Send className="h-4 w-4" />
                Send tilmelding nu
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
}
